import React from 'react';

import { twMerge } from 'tailwind-merge';

import Typography from 'components-new/typography/typography';
import type { IconProps } from 'components-new/icon/icon';
import Icon from 'components-new/icon/icon';

interface TextAreaProps extends React.HTMLProps<HTMLTextAreaElement> {
  title?: string;
  className?: string;
  textareaClassName?: string;
  error?: string;
  errorClassName?: string;
  iconClassName?: string;
  iconProps?: IconProps;
}

const TextArea: React.FC<TextAreaProps> = (props) => {
  const {
    title,
    iconProps,
    error,
    className,
    textareaClassName,
    errorClassName,
    iconClassName,
    disabled,
    ...inputFieldProps
  } = props;

  const containerClassName = twMerge('relative', className);
  const textareaContainerClassName = twMerge(
    'group flex align-center justify-center items-center px-3 py-2 rounded-md',
    'rounded-md bg-input-idle w-full px-3',
    'transition ease-in-out duration-200',
    'border',
    !disabled && 'hover:bg-input-hover focus-within:bg-input-active',
    !disabled &&
      !error &&
      ` border-transparent
        active:border-primary active:outline active:outline-primary-200 active:input-active
        focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1 focus:ring-offset-primary-200
        focus-within:ring-2 focus-within:ring-primary-200 focus-within:bg-input-active
        focus-within:border-primary`,
    error &&
      `border-destructive focus:border-none focus:ring-1 focus:ring-destructive-200 focus:ring-offset-1 focus:ring-offset-destructive-200
       focus-within:ring-2 focus-within:ring-destructive-200 focus-within:bg-input-active`,
  );
  const textAreaClassName = twMerge(
    'w-full outline-none bg-transparent',
    'text-sm font-body font-light tracking-normal',
    disabled && 'placeholder-gray-300',
    !disabled && 'placeholder-gray-400',
    textareaClassName,
  );

  const IconClassName = twMerge(
    'self-start mr-2',
    disabled ? 'text-gray-300' : 'text-gray-500',
    iconClassName,
  );

  return (
    <div className={containerClassName}>
      {title && (
        <Typography as="p" variant="body-medium-bold" className="mb-2">
          {title}
        </Typography>
      )}

      <div className={textareaContainerClassName}>
        {iconProps && <Icon {...iconProps} className={IconClassName} />}
        <textarea
          type="text"
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          disabled={disabled}
          {...inputFieldProps}
          className={textAreaClassName}
        />
      </div>

      {error && (
        <Typography
          as="p"
          variant="body-medium"
          className={twMerge('text-destructive mt-1', errorClassName)}
        >
          {error}
        </Typography>
      )}
    </div>
  );
};

export default TextArea;
